/**
 * Service-worker registration and request/response messaging.
 *
 * Every function here degrades to a no-op when service workers are
 * unavailable (older Safari, insecure origin, privacy modes). Offline mode
 * then simply cannot be armed — which the UI reports — but nothing about the
 * online app changes.
 */

const SW_URL = '/offline-sw.js';

let registration = null;

export function isSupported() {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof caches !== 'undefined' &&
    typeof indexedDB !== 'undefined'
  );
}

export async function register() {
  if (!isSupported()) return null;
  if (registration) return registration;
  try {
    registration = await navigator.serviceWorker.register(SW_URL);
    return registration;
  } catch (e) {
    return null;
  }
}

/** Resolve once a worker is actually controlling the page. */
async function controller() {
  if (!isSupported()) return null;
  if (navigator.serviceWorker.controller) return navigator.serviceWorker.controller;

  await register();
  const ready = await navigator.serviceWorker.ready.catch(() => null);
  if (!ready) return null;

  if (navigator.serviceWorker.controller) return navigator.serviceWorker.controller;

  // First-ever registration does not control the current page until it takes
  // over; the worker calls clients.claim() on activate, so wait briefly.
  return new Promise((resolve) => {
    const timeout = setTimeout(() => resolve(navigator.serviceWorker.controller), 3000);
    navigator.serviceWorker.addEventListener(
      'controllerchange',
      () => {
        clearTimeout(timeout);
        resolve(navigator.serviceWorker.controller);
      },
      { once: true }
    );
  });
}

/** Send a message to the worker and await its reply over a MessageChannel. */
export async function send(type, payload, timeoutMs = 120000) {
  const active = await controller();
  if (!active) return null;

  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timer = setTimeout(() => resolve(null), timeoutMs);
    channel.port1.onmessage = (event) => {
      clearTimeout(timer);
      resolve(event.data);
    };
    try {
      active.postMessage({ type, payload }, [channel.port2]);
    } catch (e) {
      clearTimeout(timer);
      resolve(null);
    }
  });
}

export const precacheShell = () => send('PRECACHE_SHELL');
export const cacheFiles = (urls) => send('CACHE_FILES', { urls });
export const armPack = (state) => send('ARM_PACK', state);
export const clearPack = () => send('CLEAR_PACK');
export const getState = () => send('GET_STATE', null, 5000);

/**
 * Remove the worker entirely.
 *
 * Used when a user clears their pack: leaving a registered worker behind that
 * intercepts navigations for someone who has opted out is exactly the kind of
 * "why is my app showing stale pages" bug that makes teams distrust service
 * workers.
 */
export async function unregister() {
  if (!isSupported()) return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
    registration = null;
  } catch (e) {
    /* nothing useful to do */
  }
}
